import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { FlatButton } from '@ui/buttons';
import { CompanySelectionInput } from '@containers/DigitalAnalyst/components/CompanySelectionInput';
import { AgentSelect } from '@containers/DigitalAnalyst/components/AgentSelect';
import { createTrigger } from '@services/triggers';
import { showErrorBanner } from '@utils/application';
import { useDispatch } from 'react-redux';

const FormWrapper = styled.div``;

const FieldRow = styled.div`
  margin-bottom: 16px;
`;

const AgentSelectWrapper = styled.div`
  width: 100%;

  select {
    width: 100%;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-radius: 4px;
    padding: 0 8px;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
    color: ${({ theme }) => theme.themeColors.text};
  }
`;

const StyledInput = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 0 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 14px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 14px;
  resize: vertical;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.text};
  margin-top: 4px;
`;

const SubmitWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  > div {
    width: 100%;
  }
`;

const TRIGGER_TYPES = {
  AGENT_EXECUTION: 'agent_execution',
  ACCESS_GRANT: 'access_grant',
};

export function CreateTriggerForm({ onCreateSuccess }) {
  const dispatch = useDispatch();

  const [selectedTag, setSelectedTag] = React.useState(null);
  const [triggerType, setTriggerType] = React.useState(TRIGGER_TYPES.AGENT_EXECUTION);
  const [agentName, setAgentName] = React.useState('');
  const [prompt, setPrompt] = React.useState('');
  const [allowConcurrency, setAllowConcurrency] = React.useState(false);
  const [shareWithUserId, setShareWithUserId] = React.useState('');
  const [createError, setCreateError] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const getDocTagId = () => {
    if (!selectedTag) return null;
    return selectedTag.doc_tag_id != null ? selectedTag.doc_tag_id : selectedTag.id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateError(null);
    const docTagId = getDocTagId();
    if (!docTagId) {
      setCreateError('Please select a tag.');
      return;
    }
    if (triggerType === TRIGGER_TYPES.AGENT_EXECUTION) {
      if (!agentName || !agentName.trim()) {
        setCreateError('Please select an agent.');
        return;
      }
    } else {
      const uid = shareWithUserId.trim();
      if (!uid) {
        setCreateError('Please enter a user ID.');
        return;
      }
      const num = parseInt(uid, 10);
      if (Number.isNaN(num) || num < 1) {
        setCreateError('User ID must be a positive number.');
        return;
      }
    }

    setIsSubmitting(true);
    let payload;
    if (triggerType === TRIGGER_TYPES.AGENT_EXECUTION) {
      payload = {
        doc_tag_id: docTagId,
        trigger_type: TRIGGER_TYPES.AGENT_EXECUTION,
        agent_name: agentName.trim(),
        allow_concurrency: allowConcurrency,
      };
      if (prompt.trim()) payload.prompt = prompt.trim();
    } else {
      payload = {
        doc_tag_id: docTagId,
        trigger_type: TRIGGER_TYPES.ACCESS_GRANT,
        share_with_user_id: parseInt(shareWithUserId.trim(), 10),
      };
    }

    const { trigger, error } = await createTrigger(payload);
    setIsSubmitting(false);
    if (trigger) {
      setSelectedTag(null);
      setAgentName('');
      setPrompt('');
      setAllowConcurrency(false);
      setShareWithUserId('');
      setCreateError(null);
      onCreateSuccess();
    } else {
      setCreateError(error || 'Failed to create trigger.');
      if (error) showErrorBanner(error, null, dispatch);
    }
  };

  return (
    <FormWrapper>
      <form onSubmit={handleSubmit}>
        <FieldRow>
          <Container bottom={8}>
            <Body5 bold>Trigger type</Body5>
          </Container>
          <RadioGroup>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="triggerType"
                checked={triggerType === TRIGGER_TYPES.AGENT_EXECUTION}
                onChange={() => setTriggerType(TRIGGER_TYPES.AGENT_EXECUTION)}
              />
              <Body5>Agent execution</Body5>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="triggerType"
                checked={triggerType === TRIGGER_TYPES.ACCESS_GRANT}
                onChange={() => setTriggerType(TRIGGER_TYPES.ACCESS_GRANT)}
              />
              <Body5>Access grant</Body5>
            </label>
          </RadioGroup>
        </FieldRow>

        <FieldRow>
          <Container bottom={8}>
            <Body5 bold>Tag</Body5>
          </Container>
          <CompanySelectionInput
            value={selectedTag}
            onAddCompany={(tag) => setSelectedTag(tag)}
            onRemoveCompany={() => setSelectedTag(null)}
            placeholder="Search for a tag..."
            onlyCompanyTags
          />
        </FieldRow>

        {triggerType === TRIGGER_TYPES.AGENT_EXECUTION && (
          <>
            <FieldRow>
              <Container bottom={8}>
                <Body5 bold>Agent</Body5>
              </Container>
              <AgentSelectWrapper>
                <AgentSelect value={agentName} onChange={setAgentName} />
              </AgentSelectWrapper>
            </FieldRow>
            <FieldRow>
              <Container bottom={8}>
                <Body5>Prompt (optional)</Body5>
              </Container>
              <StyledTextarea
                placeholder="e.g. Summarize the key financial metrics..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </FieldRow>
            <FieldRow>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={allowConcurrency}
                  onChange={(e) => setAllowConcurrency(e.target.checked)}
                />
                <Body5>Allow concurrency</Body5>
              </CheckboxLabel>
            </FieldRow>
          </>
        )}

        {triggerType === TRIGGER_TYPES.ACCESS_GRANT && (
          <FieldRow>
            <Container bottom={8}>
              <Body5 bold>User ID</Body5>
            </Container>
            <StyledInput
              type="number"
              min={1}
              placeholder="e.g. 1057"
              value={shareWithUserId}
              onChange={(e) => setShareWithUserId(e.target.value)}
            />
          </FieldRow>
        )}

        {createError && (
          <FieldRow>
            <ErrorText>{createError}</ErrorText>
          </FieldRow>
        )}

        <SubmitWrapper>
          <Container top={8} style={{ textAlign: 'center' }}>
            <FlatButton type="submit" disabled={isSubmitting} fullWidth>
              {isSubmitting ? 'Creating...' : 'Create trigger'}
            </FlatButton>
          </Container>
        </SubmitWrapper>
      </form>
    </FormWrapper>
  );
}
